/**
 * Query Keys Factory
 *
 * Centralized query key management for React Query.
 * Uses hierarchical structure for easy cache invalidation.
 *
 * Benefits:
 * - Type-safe query keys
 * - Easy to find and update
 * - Consistent naming across the app
 * - Hierarchical invalidation (e.g., invalidate all category queries)
 *
 * Structure:
 * ['resource'] - All queries for a resource
 * ['resource', 'list'] - List queries
 * ['resource', 'list', filters] - Filtered list queries
 * ['resource', 'detail', id] - Detail queries
 *
 * Example usage:
 * ```ts
 * // Query
 * useQuery({ queryKey: queryKeys.categories.list(), ... })
 *
 * // Invalidate all category queries
 * queryClient.invalidateQueries({ queryKey: queryKeys.categories.all })
 *
 * // Invalidate specific category
 * queryClient.invalidateQueries({ queryKey: queryKeys.categories.detail(id) })
 * ```
 */

export const queryKeys = {
  /**
   * Categories domain
   * Includes categories, areas, and services
   */
  categories: {
    // Base key for all category-related queries
    all: ['categories'] as const,

    // List of categories with nested areas and services
    list: () => [...queryKeys.categories.all, 'list'] as const,

    // Single category with details
    detail: (id: string) =>
      [...queryKeys.categories.all, 'detail', id] as const,
  },

  /**
   * Areas domain
   * Service areas within categories
   */
  areas: {
    all: ['areas'] as const,
    list: () => [...queryKeys.areas.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.areas.all, 'detail', id] as const,

    // Areas by category
    byCategory: (categoryId: string) =>
      [...queryKeys.areas.all, 'by-category', categoryId] as const,
  },

  /**
   * Services domain
   * Individual services within areas
   */
  services: {
    all: ['services'] as const,
    list: () => [...queryKeys.services.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.services.all, 'detail', id] as const,

    // Services by area
    byArea: (areaId: string) =>
      [...queryKeys.services.all, 'by-area', areaId] as const,
  },

  /**
   * Users domain
   * Admin users and authentication
   */
  users: {
    all: ['users'] as const,
    list: () => [...queryKeys.users.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.users.all, 'detail', id] as const,

    // Current authenticated user
    me: () => [...queryKeys.users.all, 'me'] as const,
  },

  /**
   * Clients domain
   * Agency clients/customers
   */
  clients: {
    all: ['clients'] as const,
    list: (filters?: {
      page?: number;
      limit?: number;
      search?: string;
      isActive?: boolean;
      isFeatured?: boolean;
      industry?: string;
      sortBy?: string;
      sortOrder?: 'ASC' | 'DESC';
    }) =>
      filters
        ? ([...queryKeys.clients.all, 'list', filters] as const)
        : ([...queryKeys.clients.all, 'list'] as const),
    detail: (id: string) => [...queryKeys.clients.all, 'detail', id] as const,
  },

  /**
   * Cases domain
   * Client projects/case studies
   */
  cases: {
    all: ['cases'] as const,
    list: (filters?: {
      page?: number;
      limit?: number;
      search?: string;
      categoryId?: string;
      status?: string;
      isFeatured?: boolean;
      sortBy?: string;
      sortOrder?: 'ASC' | 'DESC';
    }) =>
      filters
        ? ([...queryKeys.cases.all, 'list', filters] as const)
        : ([...queryKeys.cases.all, 'list'] as const),
    detail: (id: string) => [...queryKeys.cases.all, 'detail', id] as const,
  },

  /**
   * Tags domain
   * Content tags and taxonomy
   */
  tags: {
    all: ['tags'] as const,
    list: (filters?: {
      category?: string;
      search?: string;
      active?: boolean;
      lang?: 'en' | 'fr';
    }) =>
      filters
        ? ([...queryKeys.tags.all, 'list', filters] as const)
        : ([...queryKeys.tags.all, 'list'] as const),
    detail: (id: string) => [...queryKeys.tags.all, 'detail', id] as const,
    stats: () => [...queryKeys.tags.all, 'stats'] as const,
  },

  /**
   * Metrics domain
   * Analytics and performance metrics
   */
  metrics: {
    all: ['metrics'] as const,

    // Dashboard metrics
    dashboard: () => [...queryKeys.metrics.all, 'dashboard'] as const,

    // Case metrics
    case: (caseId: string) =>
      [...queryKeys.metrics.all, 'case', caseId] as const,

    // Client metrics
    client: (clientId: string) =>
      [...queryKeys.metrics.all, 'client', clientId] as const,
  },

  /**
   * Media domain
   * Images, videos, and other assets
   */
  media: {
    all: ['media'] as const,
    list: (filters?: { type?: string; folderId?: string }) =>
      filters
        ? ([...queryKeys.media.all, 'list', filters] as const)
        : ([...queryKeys.media.all, 'list'] as const),
    detail: (id: string) => [...queryKeys.media.all, 'detail', id] as const,
  },

  /**
   * Settings domain
   * Application settings and configuration
   */
  settings: {
    all: ['settings'] as const,
    general: () => [...queryKeys.settings.all, 'general'] as const,
    seo: () => [...queryKeys.settings.all, 'seo'] as const,
    integration: () => [...queryKeys.settings.all, 'integration'] as const,
  },

  /**
   * Activities domain
   * Real-time activity feed
   */
  activities: {
    all: ['activities'] as const,
    feed: () => [...queryKeys.activities.all, 'feed'] as const,
  },

  /**
   * Analytics domain
   * Dashboard KPIs, metrics, and insights
   */
  analytics: {
    all: ['analytics'] as const,
    dashboard: () => [...queryKeys.analytics.all, 'dashboard'] as const,
    activity: (page?: number, limit?: number) =>
      page && limit
        ? ([...queryKeys.analytics.all, 'activity', page, limit] as const)
        : ([...queryKeys.analytics.all, 'activity'] as const),
    period: (period?: '7D' | '30D' | '90D' | '1Y') =>
      period
        ? ([...queryKeys.analytics.all, 'period', period] as const)
        : ([...queryKeys.analytics.all, 'period'] as const),
  },

  /**
   * Taxonomies domain
   * Flexible categorization system (Services, Industries, Technologies, etc.)
   */
  taxonomies: {
    all: ['taxonomies'] as const,
    list: (filters?: {
      search?: string;
      isActive?: boolean;
      includeTerms?: boolean;
      includeTermCounts?: boolean;
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: 'ASC' | 'DESC';
    }) =>
      filters
        ? ([...queryKeys.taxonomies.all, 'list', filters] as const)
        : ([...queryKeys.taxonomies.all, 'list'] as const),
    detail: (idOrSlug: string) =>
      [...queryKeys.taxonomies.all, 'detail', idOrSlug] as const,
  },

  /**
   * Terms domain
   * Individual terms within taxonomies
   */
  terms: {
    all: ['terms'] as const,
    list: (filters?: {
      search?: string;
      taxonomyId?: string;
      taxonomySlug?: string;
      isActive?: boolean;
      includeTaxonomy?: boolean;
      includeUsageCounts?: boolean;
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: 'ASC' | 'DESC';
    }) =>
      filters
        ? ([...queryKeys.terms.all, 'list', filters] as const)
        : ([...queryKeys.terms.all, 'list'] as const),
    detail: (idOrSlug: string) =>
      [...queryKeys.terms.all, 'detail', idOrSlug] as const,
    grouped: () => [...queryKeys.terms.all, 'grouped'] as const,
    byTaxonomy: (taxonomySlug: string) =>
      [...queryKeys.terms.all, 'by-taxonomy', taxonomySlug] as const,
  },
} as const;

/**
 * Helper function to create custom query keys that follow the same pattern
 *
 * @example
 * const customKeys = createQueryKeys('custom-domain', {
 *   list: () => ['list'] as const,
 *   detail: (id: string) => ['detail', id] as const,
 * });
 */
export function createQueryKeys<
  T extends Record<string, (...args: unknown[]) => readonly unknown[]>,
>(domain: string, keys: T): { all: readonly [string] } & T {
  const all = [domain] as const;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result: any = { all };

  for (const [key, fn] of Object.entries(keys)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    result[key] = (...args: any[]) => [...all, ...fn(...args)];
  }

  return result;
}
